import { useListState } from "@/hooks/use-list-state";
import { useEffect, useState } from "react";
import { residentEvidenceLabel } from "@shared/broadcast-recipient-review";
import { FinancialEvidence } from "@/components/member-financials/financial-observations";
import { FinancialThresholdSettings } from "@/components/member-financials/financial-threshold-settings";
import { useMemberFinancials } from "@/components/member-financials/use-member-financials";
import { compareFinancialEvidence } from "@/components/member-financials/financial-sort";
import { MemberSortHeader } from "@/components/member-financials/member-sort-header";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchQueryParam, useSearchFilterOverride } from "@/hooks/use-search-query-param";
import { matchesSearch } from "@/lib/global-search";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useSearch, Link } from "wouter";
import { rowNavHandlers } from "@/lib/row-nav";
import AppLayout from "@/components/app-layout";
import { MemberSourceBadge } from "@/components/member-source-badge";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePermissions, ViewOnlyBanner } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { Search, Plus, Users, Layers, TriangleAlert, Star } from "lucide-react";
import { BulkImportExportDialog } from "@/components/bulk-import-export-dialog";
import { MemberPortalOptInButton } from "@/components/member-portal-beta";
import type { Member, Property } from "@shared/schema";
import { MEMBER_TYPES, type MemberType } from "@shared/schema";
import { sortProperties } from "@/lib/property-utils";
import { cn } from "@/lib/utils";
import { getMemberDisplayName, getMemberRating } from "@shared/member-display";
import { getPadsplitProfileUrl } from "@shared/padsplit-profile-url";
import { BackToPropertyLink } from "@/components/back-to-property-link";
import { bucketMemberStatus, MEMBER_STATUS_OPTIONS } from "@shared/member-status";
import { formatDateOnly } from "@/lib/format-date-only";
import { memberLifecycle, memberVisible, formatMemberTenure, lifecycleStatusBucket } from "@shared/member-lifecycle";

type MemberWithCurrent = Member & {
  residency?: import("@shared/broadcast-recipient-review").MemberResidencyView | null;
  currentPropertyId?: number | null;
  roomNumber?: string | null;
  /** Resolved server-side by id. A member can sit on a property the caller
   *  does not own (cross-account link, stale tenancy row), which the per-user
   *  property list cannot name — hence the bare "Property #66" this replaces. */
  propertyName?: string | null;
  propertyAddress?: string | null;
};
import { JourneyEmptyState, useJourneyStage } from "@/lib/journey-empty-states";
import { usePageTitle } from "@/hooks/use-page-title";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";

export default function AppMembers() {
  return (
    <AppLayout>
      <MembersContent />
    </AppLayout>
  );
}

// Permission-dependent hooks must run below AppLayout's PermissionsProvider.
function MembersContent() {
  const queryClient = useQueryClient();
  usePageTitle("Members");
  const [pathname, navigate] = useLocation();
  const urlSearch = useSearch();
  const { canEdit: canEditArea } = usePermissions();
  const readOnly = !canEditArea("members");
  const urlParams = new URLSearchParams(urlSearch);
  const initialPropertyId = urlParams.get("propertyId");

  const [search, setSearch, incomingSearch] = useSearchQueryParam();
  const propertyFilter = urlParams.get("propertyId") || "all";
  const setPropertyFilter = (v: string) => {
    const next = new URLSearchParams(urlSearch);
    if (v === "all") next.delete("propertyId");
    else next.set("propertyId", v);
    const qs = next.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
  };
  const [typeFilter, setTypeFilterState] = useListState<string>("members:type",
    () => incomingSearch !== null ? "all" : localStorage.getItem("pp:members-type-filter") ?? "all",
    incomingSearch ?? "",
  );
  const setTypeFilter = (next: string) => {
    localStorage.setItem("pp:members-type-filter", next);
    setTypeFilterState(next);
  };
  // Task #1996 — Members list defaults to active-only. Hosts can toggle this
  // on to also see members PadSplit reported as moved-out (or that the
  // missing-from-scrape diff archived). Historical data stays viewable via
  // the member detail page regardless of the toggle.
  const [showInactive, setShowInactiveState] = useListState<boolean>("members:inactive",
    () => incomingSearch !== null || localStorage.getItem("pp:members-show-archived") === "true",
    incomingSearch ?? "",
  );
  const setShowInactive = (next: boolean) => {
    localStorage.setItem("pp:members-show-archived", String(next));
    setShowInactiveState(next);
  };
  const [showArchivedValue, setShowArchivedValue] = useSearchFilterOverride(incomingSearch, useLocalStorageState<"true" | "false">(
    "pp:members-show-archived-applicants", "false", (stored) => stored === "true" ? "true" : "false",
  ), "true", "members:archived-override");
  const showArchived = showArchivedValue === "true";
  const setShowArchived = (value: boolean) => setShowArchivedValue(value ? "true" : "false");
  // Quick status tab persists in the URL (?tab=…) so each tab is linkable
  // and survives refresh / back-forward navigation.
  type QuickFilter = "all" | "moving_in" | "moved_out" | "booking_request" | "terminated";
  const QUICK_FILTER_VALUES: QuickFilter[] = ["all", "moving_in", "moved_out", "booking_request", "terminated"];
  const rawTab = urlParams.get("tab");
  const quickFilter: QuickFilter = QUICK_FILTER_VALUES.includes(rawTab as QuickFilter) ? (rawTab as QuickFilter) : "all";
  const setQuickFilter = (v: QuickFilter) => {
    const next = new URLSearchParams(urlSearch);
    if (v === "all") next.delete("tab");
    else next.set("tab", v);
    const qs = next.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname);
  };
  const [sortBy, setSortBy] = useLocalStorageState<string>(
    "pp:members-sort-by", "name",
    (stored) => ["name", "type", "propertyStatus", "room", "status", "moveInDate", "tenure", "moveOutDate", "rating", "financialStatus", "balance"].includes(stored) ? stored : "name",
  );
  const [sortDir, setSortDir] = useLocalStorageState<"asc" | "desc">(
    "pp:members-sort-dir",
    "asc",
    (stored) => (stored === "desc" ? "desc" : "asc"),
  );
  const [showDialog, setShowDialog] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<MemberWithCurrent | null>(null);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [form, setForm] = useState<{
    name: string;
    email: string;
    phone: string;
    propertyId: string;
    roomId: string;
    status: string;
    type: MemberType;
  }>({
    name: "",
    email: "",
    phone: "",
    propertyId: "",
    roomId: "",
    status: "active",
    type: "direct",
  });

  function toggleSort(field: string) {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir(
        field === "tenure" || field === "moveInDate"
          ? "desc"
          : "asc",
      );
    }
  }

  const { toast } = useToast();

  const membersQuery = useQuery<MemberWithCurrent[]>({
    queryKey: ["/api/app/devices/members"],
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const roomsQuery = useQuery<{ id: number; roomNumber: string; shadowOfRoomId?: number | null }[]>({
    queryKey: ["/api/app/properties", form.propertyId, "rooms", "bedroomOnly"],
    queryFn: async () => {
      // Task #4157 — bedroomOnly so the member room picker only ever offers real
      // bedrooms (never bathrooms or shadow rows). A member must never be linked
      // to a non-bedroom; the write routes reject it server-side too.
      const res = await fetch(`/api/app/properties/${form.propertyId}/rooms?bedroomOnly=true`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!form.propertyId,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const propertyId = form.propertyId ? parseInt(form.propertyId, 10) : null;
      const roomId = form.roomId ? parseInt(form.roomId, 10) : null;
      const res = await apiRequest("POST", "/api/app/devices/members", {
        name: form.name,
        email: form.email || null,
        phone: form.phone,
        roomId,
        status: form.status,
        type: form.type,
      });
      const created = (await res.json()) as { id: number };
      // Task #1447 — property assignment is its own endpoint. Apply it
      // once the member row exists.
      if (created?.id != null && propertyId != null) {
        await apiRequest("PUT", `/api/app/devices/members/${created.id}/property`, {
          propertyId,
          roomId,
        });
      }
      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/members"] });
      setShowDialog(false);
      resetForm();
      toast({ title: "Member added" });
    },
    onError: (error: Error) => {
      console.error("Add member error:", error);
      toast({ title: "Failed to add member", description: parseErrorMessage(error, "Couldn't add this member. Please try again."), variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingMember) return;
      const nextPropertyId = form.propertyId ? parseInt(form.propertyId, 10) : null;
      const nextRoomId = form.roomId ? parseInt(form.roomId, 10) : null;
      // Task #1447 — property assignment goes through its own endpoint.
      // Push it first so the room/property consistency check on the PATCH
      // route sees the new property when the room is also changing.
      if (nextPropertyId !== (editingMember.currentPropertyId ?? null)) {
        await apiRequest("PUT", `/api/app/devices/members/${editingMember.id}/property`, {
          propertyId: nextPropertyId,
          roomId: nextRoomId,
        });
      }
      return apiRequest("PATCH", `/api/app/devices/members/${editingMember.id}`, {
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        roomId: nextRoomId,
        status: form.status,
        type: form.type,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/members"] });
      setShowDialog(false);
      setEditingMember(null);
      resetForm();
      toast({ title: "Member updated" });
    },
    onError: () => {
      toast({ title: "Failed to update member", variant: "destructive" });
    },
  });

  function resetForm() {
    setForm({ name: "", email: "", phone: "", propertyId: "", roomId: "", status: "active", type: "direct" });
    setTouched(new Set());
  }

  function touch(field: string) {
    setTouched((prev) => new Set([...prev, field]));
  }

  const allMembers = membersQuery.data || [];
  const allProperties = propertiesQuery.data || [];
  const properties = sortProperties(allProperties.filter((p) => p.status === "active"));
  const journeyStage = useJourneyStage();

  const lifecycleById = new Map(allMembers.map((m) => [m.id, memberLifecycle(m)]));
  const lifecycle = (m: MemberWithCurrent) => lifecycleById.get(m.id)!;

  const resolveCurrentPropertyId = (m: MemberWithCurrent): number | null =>
    (m.currentPropertyId ?? null);

  const visibleMembers = allMembers
    .filter((m) => {
      if (!memberVisible(m, showInactive, showArchived)) return false;
      if (propertyFilter !== "all" && String(resolveCurrentPropertyId(m)) !== propertyFilter) return false;
      if (typeFilter !== "all" && (m.type ?? "direct") !== typeFilter) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return matchesSearch(s, [
        getMemberDisplayName(m), m.displayName, m.name, m.legalName, m.email, m.phone,
        m.carMake, m.carModel, m.carYear, m.carColor, m.licensePlate, m.licensePlateState,
        lifecycle(m).label, lifecycle(m).outcome, "member members resident residents tenant tenants",
      ]);
    });
  const countBucket = (bucket: string) => visibleMembers.filter((m) =>
    !lifecycle(m).archivedApplicant && lifecycleStatusBucket(m) === bucket).length;
  const movingInCount = countBucket("moving_in");
  const movingOutCount = countBucket("moving_out");
  const bookingRequestCount = countBucket("booking_request");
  const terminatedCount = countBucket("terminated");
  const allMembersCount = visibleMembers.length;

  const filteredMembers = visibleMembers.filter((m) => {
      // Reads the APP-OWNED retirement stamp, so a booking request the
      // harvest keeps re-stamping as "request" stays out of the tab.
      const bucket = lifecycleStatusBucket(m);
      if (quickFilter !== "all" && lifecycle(m).archivedApplicant) return false;
      // Quick status filter — scopes list to the chosen status bucket.
      if (quickFilter === "moving_in") {
        if (bucket !== "moving_in") return false;
      } else if (quickFilter === "moved_out") {
        if (bucket !== "moving_out") return false;
      } else if (quickFilter === "booking_request") {
        if (bucket !== "booking_request") return false;
      } else if (quickFilter === "terminated") {
        if (bucket !== "terminated") return false;
      }
      return true;
    });
  // Fetch inputs depend only on filtering, never on evidence-dependent ordering.
  const financials = useMemberFinancials(filteredMembers.map(member => member.id));
  const filtered = [...filteredMembers].sort((a, b) => {
      if (sortBy === "financialStatus" || sortBy === "balance") {
        const field = sortBy === "financialStatus" ? "financial_status" : "balance";
        return compareFinancialEvidence(
          financials.members.get(a.id)?.fields[field],
          financials.members.get(b.id)?.fields[field], field, sortDir,
        );
      }
      let aVal: string | number;
      let bVal: string | number;
      if (sortBy === "tenure") {
        const aDays = lifecycle(a).tenureDays;
        const bDays = lifecycle(b).tenureDays;
        if (aDays === null) return bDays === null ? 0 : 1;
        if (bDays === null) return -1;
        aVal = aDays;
        bVal = bDays;
      } else if (sortBy === "moveInDate") {
        const aDate = lifecycle(a).actualMoveInDate;
        const bDate = lifecycle(b).actualMoveInDate;
        if (!aDate) return !bDate ? 0 : 1;
        if (!bDate) return -1;
        aVal = aDate.getTime();
        bVal = bDate.getTime();
      } else if (sortBy === "moveOutDate") {
        // Members without a move-out date sort to the end regardless of direction.
        const aT = lifecycle(a).actualMoveOutDate?.getTime() ?? null;
        const bT = lifecycle(b).actualMoveOutDate?.getTime() ?? null;
        if (aT === null && bT === null) return 0;
        if (aT === null) return 1;
        if (bT === null) return -1;
        return sortDir === "asc" ? aT - bT : bT - aT;
      } else if (sortBy === "room") {
        // Room numbers can be alphanumeric (e.g. "1", "10", "A2"), so compare
        // with localeCompare's numeric option for natural ordering. Members
        // without a room sort to the end regardless of direction.
        const aRoom = a.roomNumber || "";
        const bRoom = b.roomNumber || "";
        if (!aRoom && !bRoom) return 0;
        if (!aRoom) return 1;
        if (!bRoom) return -1;
        const cmp = aRoom.localeCompare(bRoom, undefined, { numeric: true, sensitivity: "base" });
        return sortDir === "asc" ? cmp : -cmp;
      } else if (sortBy === "type") {
        aVal = (a.type ?? "direct").toLowerCase();
        bVal = (b.type ?? "direct").toLowerCase();
      } else if (sortBy === "status") {
        aVal = lifecycle(a).label;
        bVal = lifecycle(b).label;
      } else if (sortBy === "propertyStatus") {
        // Keep the persisted key, but compare the text rendered in the Property cells.
        const aId = resolveCurrentPropertyId(a);
        const bId = resolveCurrentPropertyId(b);
        aVal = aId ? getPropertyName(aId, a.propertyName, a.propertyAddress).toLowerCase() : "";
        bVal = bId ? getPropertyName(bId, b.propertyName, b.propertyAddress).toLowerCase() : "";
        if (!aVal) return !bVal ? 0 : 1;
        if (!bVal) return -1;
      } else if (sortBy === "rating") {
        const aRating = getMemberRating(a);
        const bRating = getMemberRating(b);
        // Members without a usable rating stay at the bottom in either direction.
        if (aRating === null) return bRating === null ? 0 : 1;
        if (bRating === null) return -1;
        return sortDir === "asc" ? aRating - bRating : bRating - aRating;
      } else {
        aVal = getMemberDisplayName(a).toLowerCase();
        bVal = getMemberDisplayName(b).toLowerCase();
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const tenureRankMap: Record<number, number> = (() => {
    const byProperty: Record<string, MemberWithCurrent[]> = {};
    for (const m of filtered) {
      const key = String(resolveCurrentPropertyId(m) ?? "none");
      if (!byProperty[key]) byProperty[key] = [];
      byProperty[key].push(m);
    }
    const rankMap: Record<number, number> = {};
    for (const group of Object.values(byProperty)) {
      const withDate = group
        .filter((m) => {
          const days = lifecycle(m).tenureDays;
          return days !== null && days >= 0;
        })
        .sort((a, b) => {
          return (lifecycle(b).tenureDays ?? 0) - (lifecycle(a).tenureDays ?? 0) || a.id - b.id;
        });
      withDate.forEach((m, i) => { rankMap[m.id] = i + 1; });
    }
    return rankMap;
  })();

  // Detect duplicate room occupancy: two or more active members in the same
  // property and same room number. This is a data-integrity issue that can arise
  // from sync or import errors.
  const duplicateRoomSet: Set<string> = (() => {
    const counts: Record<string, number> = {};
    for (const m of allMembers) {
      const propId = resolveCurrentPropertyId(m);
      if (!propId || !m.roomNumber || lifecycleStatusBucket(m) !== "active") continue;
      const key = `${propId}|${m.roomNumber}`;
      counts[key] = (counts[key] || 0) + 1;
    }
    const result = new Set<string>();
    for (const [key, count] of Object.entries(counts)) {
      if (count > 1) result.add(key);
    }
    return result;
  })();

  function getPropertyName(
    propId: number | null,
    serverName?: string | null,
    serverAddress?: string | null,
  ) {
    if (!propId) return "—";
    if (serverName || serverAddress) return serverName || serverAddress || "";
    const prop = allProperties.find((p) => p.id === propId);
    return prop?.name || prop?.address || `Property #${propId}`;
  }

  return (
    <>
      <div className="p-6">
        <ViewOnlyBanner area="members" />
        {initialPropertyId ? (
          <BackToPropertyLink propertyId={initialPropertyId} tab="members" />
        ) : null}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-y-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-members-title" data-tour="members-heading">Members</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your co-living members across all properties.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <FinancialThresholdSettings initialPropertyId={propertyFilter === "all" ? 0 : Number(propertyFilter)} />
            <Button
              className="bg-brand hover:bg-brand-hover text-white"
              disabled={readOnly}
              onClick={() => { setEditingMember(null); resetForm(); setShowDialog(true); }}
              data-testid="button-add-member"
              data-tour="members-add"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Member
            </Button>
            <Button
              variant="outline"
              onClick={() => setBulkOpen(true)}
              data-testid="button-bulk-import-export"
            >
              <Layers className="h-4 w-4 mr-1" /> Bulk Import / Export
            </Button>
            {/* B14 — member portal account opt-in. */}
            <MemberPortalOptInButton />
            <BulkImportExportDialog
              open={bulkOpen}
              onOpenChange={setBulkOpen}
              title="Bulk Import / Export"
              description="Add many direct members at once from a spreadsheet, or export your current list."
              tabs={[
                {
                  value: "members",
                  label: "Members",
                  exportUrl: "/api/app/members/bulk/export",
                  importUrl: "/api/app/members/bulk",
                  invalidateQueryKey: ["/api/app/devices/members"],
                  entityNounPlural: "members",
                },
              ]}
            />
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="members" />
              <GuideButton pageKey="members" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="members" />
        </div>

        {/* Quick status filter tab row — styled like PadSplit */}
        <div className="flex items-center gap-0 border-b border-gray-200 mb-4">
          <button
            onClick={() => setQuickFilter("all")}
            className={cn(
              "px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors",
              quickFilter === "all"
                ? "border-brand text-gray-900 font-bold"
                : "border-transparent text-gray-500 hover:text-gray-900",
            )}
            data-testid="filter-tab-all-members"
          >
            All members ({allMembersCount})
          </button>
          <button
            onClick={() => setQuickFilter("moving_in")}
            className={cn(
              "px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors",
              quickFilter === "moving_in"
                ? "border-brand text-gray-900 font-bold"
                : "border-transparent text-gray-500 hover:text-gray-900",
            )}
            data-testid="filter-tab-moving-in"
          >
            Moving in ({movingInCount})
          </button>
          <button
            onClick={() => setQuickFilter("moved_out")}
            className={cn(
              "px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors",
              quickFilter === "moved_out"
                ? "border-brand text-gray-900 font-bold"
                : "border-transparent text-gray-500 hover:text-gray-900",
            )}
            data-testid="filter-tab-moving-out"
          >
            Moving out ({movingOutCount})
          </button>
          <button
            onClick={() => setQuickFilter("booking_request")}
            className={cn(
              "px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors",
              quickFilter === "booking_request"
                ? "border-brand text-gray-900 font-bold"
                : "border-transparent text-gray-500 hover:text-gray-900",
            )}
            data-testid="filter-tab-booking-requests"
          >
            Booking requests ({bookingRequestCount})
          </button>
          <button
            onClick={() => setQuickFilter("terminated")}
            className={cn(
              "px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors",
              quickFilter === "terminated"
                ? "border-brand text-gray-900 font-bold"
                : "border-transparent text-gray-500 hover:text-gray-900",
            )}
            data-testid="filter-tab-terminated"
          >
            Terminated ({terminatedCount})
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between mb-4 gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <SearchableSelect
              value={propertyFilter}
              onValueChange={setPropertyFilter}
              placeholder="All Properties"
              searchPlaceholder="Search properties..."
              emptyText="No properties match."
              testId="select-property-filter"
              className="w-[200px] h-9"
              triggerProps={{
                "data-tour": "members-filters",
                title:
                  propertyFilter === "all"
                    ? undefined
                    : (() => {
                        const p = properties.find((p) => String(p.id) === propertyFilter);
                        return p ? p.name || p.address || `Property #${p.id}` : undefined;
                      })(),
              }}
              options={[
                { value: "all", label: "All Properties" },
                ...properties.map((p) => ({
                  value: String(p.id),
                  label: p.name || p.address || `Property #${p.id}`,
                  keywords: `${p.name ?? ""} ${p.address ?? ""}`,
                })),
              ]}
            />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px] h-9" data-testid="select-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {MEMBER_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t === "direct" ? "Direct" : "PadSplit"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label
              className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none"
              data-testid="label-show-inactive"
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                data-testid="checkbox-show-inactive"
              />
              Show Inactive
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300"
                checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
                data-testid="checkbox-show-archived" />
              Show Archived
            </label>
          </div>
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search members..."
              className="pl-9 w-full sm:w-[280px] h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="input-members-search"
            />
          </div>
        </div>

        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <p className="font-semibold">Financial observations: dry-run only. Messaging disabled.</p>
          <p className="mt-1">Unknown is not zero. Balances are credit-positive only when supported by approved source evidence; these are not live balances. Existing Collections settings and sends are unchanged.</p>
        </div>
        {financials.error && <div role="alert" className="mb-3 flex flex-wrap items-center gap-2 text-sm text-red-700">
          <span>{parseErrorMessage(financials.error, "Financial observations could not be loaded.")}</span>
          <Button variant="outline" size="sm" disabled={financials.isFetching} onClick={() => void financials.reload()}>Retry observations</Button>
        </div>}
        <div className="border border-gray-200 rounded-lg overflow-x-auto" data-tour="members-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[
                  ["name", "Name", "sort-name"],
                  ["type", "Type", "sort-type"],
                  ["propertyStatus", "Property", "sort-property-status"],
                  ["room", "Room", "sort-room"],
                  ["rating", "Rating", "sort-rating"],
                  ["financialStatus", "Financial status", "sort-financial-status"],
                  ["balance", "Balance (credit-positive)", "sort-balance"],
                  ["status", "Status", "sort-status"],
                  ["moveInDate", "Move-in", "sort-move-in"],
                  ["tenure", "Tenure", "sort-tenure"],
                  ["moveOutDate", "Move-out", "sort-move-out"],
                ].map(([field, label, testId]) => (
                  <MemberSortHeader key={field} field={field} label={label} testId={testId}
                    sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {membersQuery.isLoading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-sm text-gray-400">Loading...</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-6">
                    {allMembers.length === 0 ? (
                      <JourneyEmptyState
                        page="members"
                        stage={journeyStage}
                        icon={<Users className="h-10 w-10 text-gray-300" />}
                        primaryAction={{ onClick: () => { setEditingMember(null); resetForm(); setShowDialog(true); } }}
                      />
                    ) : (
                      <div className="text-center py-6" data-testid="empty-members-filtered">
                        <Users className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                        <p className="text-sm text-gray-400">No members match your filters.</p>
                      </div>
                    )}
                  </td>
                </tr>
              ) : filtered.map((member) => {
                const profileUrl = getPadsplitProfileUrl({ occupantId: member.occupantId });
                const memberRating = getMemberRating(member);
                const ratingStarColor = memberRating === null
                  ? ""
                  : memberRating >= 4.2
                    ? "fill-green-500 text-green-500"
                    : memberRating >= 3
                      ? "fill-amber-400 text-amber-400"
                      : "fill-red-500 text-red-500";
                return (
                <tr key={member.id} className="hover:bg-gray-50 cursor-pointer" {...rowNavHandlers(`/app/members/${member.id}`, navigate)} data-testid={`row-member-${member.id}`}>
                  <td className="px-4 py-3 font-medium text-brand hover:underline" data-testid={`text-member-name-${member.id}`}>
                    <div className="flex items-center gap-1 -mx-4 -my-3 px-4 py-3">
                      <Link href={`/app/members/${member.id}`} onClick={(e) => e.stopPropagation()}>
                        {getMemberDisplayName(member)}
                      </Link>
                      {profileUrl && (
                        <a
                          href={profileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-500 hover:underline ml-1"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`link-padsplit-profile-${member.id}`}
                        >↗</a>
                      )}
                      {!profileUrl && (member.type ?? "direct") === "padsplit" && (
                        <span className="text-xs text-gray-500 ml-1" data-testid={`text-padsplit-profile-unavailable-${member.id}`}>Profile unavailable</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3" data-testid={`text-member-type-${member.id}`}>
                    <MemberSourceBadge
                      padsplitMemberId={
                        (member.type ?? "direct") === "padsplit"
                          ? member.padsplitMemberId ?? "padsplit"
                          : null
                      }
                      memberId={member.id}
                      size="sm"
                      href={profileUrl}
                    />
                  </td>
                  <td className="px-4 py-3 text-gray-600" data-testid={`text-member-property-${member.id}`}>{getPropertyName(resolveCurrentPropertyId(member), member.propertyName, member.propertyAddress)}</td>
                  <td className="px-4 py-3 text-gray-600" data-testid={`text-member-room-${member.id}`}>
                    <div className="flex items-center gap-1.5">
                      {member.roomNumber || "—"}
                      {(() => {
                        const propId = resolveCurrentPropertyId(member);
                        if (propId && member.roomNumber && duplicateRoomSet.has(`${propId}|${member.roomNumber}`)) {
                          return (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700"
                              title="Duplicate room: another member is also assigned to this room"
                              data-testid={`badge-duplicate-room-${member.id}`}
                            >
                              <TriangleAlert className="h-3 w-3" />
                              Dup
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700" data-testid={`text-member-rating-${member.id}`}>
                    {memberRating === null ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 tabular-nums">
                        <Star
                          aria-hidden="true"
                          className={`h-3.5 w-3.5 ${ratingStarColor}`}
                          data-testid={`icon-member-rating-${member.id}`}
                        />
                        {memberRating.toFixed(1)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top" data-testid={`text-member-financial-status-${member.id}`}>
                    {financials.isPending && financials.members.get(member.id)?.fields.financial_status.reason === "Source observations are loading."
                      ? <Skeleton className="h-5 w-28" aria-label="Loading financial status" />
                      : <FinancialEvidence compact datum={financials.members.get(member.id)?.fields.financial_status} />}
                  </td>
                  <td className="px-4 py-3 align-top" data-testid={`text-member-balance-${member.id}`}>
                    {financials.isPending && financials.members.get(member.id)?.fields.balance.reason === "Source observations are loading."
                      ? <Skeleton className="h-5 w-28" aria-label="Loading balance" />
                      : <FinancialEvidence compact datum={financials.members.get(member.id)?.fields.balance} />}
                  </td>
                  <td className="px-4 py-3">
                    <span className="block text-xs text-gray-500">Stored lifecycle</span>
                    <span data-testid={`text-member-status-${member.id}`}>
                    {lifecycle(member).archivedApplicant ? (
                      <span className="text-xs text-gray-600" data-testid={`badge-never-moved-in-${member.id}`}>
                        Never moved in
                        {lifecycle(member).outcome && <span className="block capitalize text-gray-400">{lifecycle(member).outcome}</span>}
                      </span>
                    ) : lifecycle(member).bucket === "terminated" ? (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700"
                        data-testid={`badge-terminated-${member.id}`}
                      >
                        {lifecycle(member).label}
                      </span>
                    ) : (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        lifecycle(member).bucket === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                      }`}>
                        {lifecycle(member).label}
                      </span>
                    )}
                    </span>
                    <span className="block max-w-xs text-xs text-gray-500 mt-1" data-testid={`text-member-residency-${member.id}`}>
                      {residentEvidenceLabel(member.residency)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-700" data-testid={`text-member-movein-${member.id}`}>
                    {formatDateOnly(lifecycle(member).actualMoveInDate) ?? "—"}
                    {lifecycle(member).plannedMoveInDate && (
                      <span className="block text-gray-400">Planned: {formatDateOnly(lifecycle(member).plannedMoveInDate)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`text-member-tenure-${member.id}`}>
                    {lifecycle(member).tenureDays !== null ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium text-gray-700">{formatMemberTenure(lifecycle(member).tenureDays)}</span>
                        {tenureRankMap[member.id] !== undefined && (
                          <span className="text-xs text-gray-400 font-normal">#{tenureRankMap[member.id]}</span>
                        )}
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-700" data-testid={`text-member-moveout-${member.id}`}>
                    {formatDateOnly(lifecycle(member).actualMoveOutDate) ?? "—"}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) { setEditingMember(null); resetForm(); } }}>
        <DialogContent
          className="max-h-[90vh] overflow-y-auto"
          data-testid="dialog-member"
          // The Select dropdowns render into a Radix portal that sits outside
          // the Dialog DOM node. Without these guards the Dialog sees clicks
          // inside the dropdown as "outside" interactions and closes itself
          // before the host can pick an option.
          onInteractOutside={(e) => {
            const target = e.target as Element | null;
            if (target?.closest("[data-radix-popper-content-wrapper]")) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            const target = e.target as Element | null;
            if (target?.closest("[data-radix-popper-content-wrapper]")) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{editingMember ? "Edit Member" : "Add Member"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label htmlFor="member-name">Name <span className="text-red-500">*</span></Label>
              <Input
                id="member-name"
                placeholder="Full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                onBlur={() => touch("name")}
                data-testid="input-member-name"
              />
              {touched.has("name") && !form.name && (
                <p className="text-xs text-red-500 mt-1" data-testid="error-member-name">Name is required</p>
              )}
            </div>
            <div>
              <Label htmlFor="member-email">Email</Label>
              <Input
                id="member-email"
                type="email"
                placeholder="email@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="input-member-email"
              />
            </div>
            <div>
              <Label htmlFor="member-phone">Phone <span className="text-red-500">*</span></Label>
              <Input
                id="member-phone"
                placeholder="(555) 123-4567"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                onBlur={() => touch("phone")}
                data-testid="input-member-phone"
              />
              {touched.has("phone") && !form.phone && (
                <p className="text-xs text-red-500 mt-1" data-testid="error-member-phone">Phone number is required</p>
              )}
            </div>
            <div>
              <Label>Property <span className="text-red-500">*</span></Label>
              <Select
                value={form.propertyId}
                onValueChange={(v) => { setForm({ ...form, propertyId: v, roomId: "" }); touch("propertyId"); }}
              >
                <SelectTrigger
                  data-testid="select-member-property"
                  title={
                    (() => {
                      const p = properties.find((p) => String(p.id) === form.propertyId);
                      return p ? p.name || p.address || `Property #${p.id}` : undefined;
                    })()
                  }
                >
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name || p.address || `Property #${p.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {touched.has("propertyId") && !form.propertyId && (
                <p className="text-xs text-red-500 mt-1" data-testid="error-member-property">Property is required</p>
              )}
            </div>
            <div>
              <Label>Room <span className="text-red-500">*</span></Label>
              <Select
                value={form.roomId}
                onValueChange={(v) => { setForm({ ...form, roomId: v }); touch("roomId"); }}
                disabled={!form.propertyId}
              >
                <SelectTrigger
                  data-testid="select-member-room"
                  title={
                    (() => {
                      const r = (roomsQuery.data || []).find((r) => String(r.id) === form.roomId);
                      return r ? r.roomNumber || undefined : undefined;
                    })()
                  }
                >
                  <SelectValue placeholder={form.propertyId ? "Select a room" : "Select a property first"} />
                </SelectTrigger>
                <SelectContent>
                  {(roomsQuery.data || []).map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>
                      {r.roomNumber}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {touched.has("roomId") && !form.roomId && form.propertyId && (
                <p className="text-xs text-red-500 mt-1" data-testid="error-member-room">Room is required</p>
              )}
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="select-member-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEMBER_STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({ ...form, type: v as MemberType })}
              >
                <SelectTrigger data-testid="select-member-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">Direct</SelectItem>
                  <SelectItem value="padsplit">PadSplit</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Direct members are texted from your messaging number. PadSplit
                members go through PadSplit Messenger.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="button-member-cancel">
                Cancel
              </Button>
              <Button
                className="bg-brand hover:bg-brand-hover text-white"
                disabled={createMutation.isPending || updateMutation.isPending}
                onClick={() => {
                  const allRequired = ["name", "phone", "propertyId", "roomId"];
                  const missingAny = !form.name || !form.phone || !form.propertyId || !form.roomId;
                  if (missingAny) {
                    setTouched(new Set(allRequired));
                    return;
                  }
                  editingMember ? updateMutation.mutate() : createMutation.mutate();
                }}
                data-testid="button-member-submit"
              >
                {(createMutation.isPending || updateMutation.isPending) ? "Saving..." : editingMember ? "Update" : "Add"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
